import { Collapse, styled, useMediaQuery } from '@mui/material'
import { useEffect, useId, useRef, useState } from 'react'
import { NavFlyout, type NavFlyoutCloseReason } from './NavFlyout'
import { ICON_SLOT_SIZE, NavItemRow, ROW_PADDING_INLINE } from './NavItem'
import { NAV_ITEM_GAP, NavList } from './NavList'
import { useSideNavContext } from './SideNavContext'
import type { MouseEvent, ReactNode } from 'react'

type NavGroupProps = {
  icon: ReactNode
  label: string
  /** One of the group's items leads to the open page. */
  selected?: boolean
  /** The total of its items' counts, shown on the header while the items are hidden. */
  count?: number
  /** The group's `NavItem`s. */
  children: ReactNode
}

// Long enough for the pointer to cross from the rail to the flyout without it closing.
const CLOSE_DELAY_MS = 150

const Items = styled(NavList)(({ theme }) => {
  // The rail runs under the centre of the header's icon.
  const railOffset = `(${theme.spacing(ROW_PADDING_INLINE)} + ${ICON_SLOT_SIZE} / 2)`
  return {
    position: 'relative',
    paddingTop: NAV_ITEM_GAP,
    paddingLeft: `calc(${railOffset} + ${theme.spacing(2)})`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: NAV_ITEM_GAP,
      bottom: 0,
      left: `calc(${railOffset} - ${theme.strokeWidth.default})`,
      borderLeft: `${theme.strokeWidth.default} solid ${theme.palette.border.subtle}`,
    },
  }
})

/**
 * A set of `NavItem`s under one header, e.g. Risk management. In the expanded nav the header expands the
 * items beneath it; on the icon rail, hovering or focusing the header opens them in a `NavFlyout`.
 */
export const NavGroup = (props: NavGroupProps) => {
  const { collapsed } = useSideNavContext()
  return collapsed ? <FlyoutGroup {...props} /> : <InlineGroup {...props} />
}

const InlineGroup = ({ icon, label, selected = false, count, children }: NavGroupProps) => {
  const [expanded, setExpanded] = useState(selected)
  const [wasSelected, setWasSelected] = useState(selected)
  // A group opens whenever it comes to hold the open page, so its selected item is in view even when the
  // page was reached from elsewhere; leaving it doesn't close it.
  if (selected !== wasSelected) {
    setWasSelected(selected)
    if (selected) setExpanded(true)
  }
  const itemsId = useId()
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  return (
    <li>
      <NavItemRow
        icon={icon}
        label={label}
        selected={selected}
        count={expanded ? undefined : count}
        aria-expanded={expanded}
        aria-controls={itemsId}
        onClick={() => setExpanded((current) => !current)}
      />
      <Collapse in={expanded} id={itemsId} timeout={reduceMotion ? 0 : undefined}>
        <Items>{children}</Items>
      </Collapse>
    </li>
  )
}

const FlyoutGroup = ({ icon, label, selected = false, count, children }: NavGroupProps) => {
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [openedByKeyboard, setOpenedByKeyboard] = useState(false)
  const closeTimer = useRef<number | undefined>(undefined)
  const flyoutId = useId()

  const cancelClose = () => window.clearTimeout(closeTimer.current)
  useEffect(() => cancelClose, [])

  const openFlyout = (byKeyboard: boolean) => {
    cancelClose()
    setOpenedByKeyboard(byKeyboard)
    setOpen(true)
  }

  const closeSoon = () => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS)
  }

  const close = (reason: NavFlyoutCloseReason) => {
    cancelClose()
    setOpen(false)
    if (reason === 'keyboard') trigger?.focus()
  }

  return (
    <li>
      <NavItemRow
        ref={setTrigger}
        icon={icon}
        label={label}
        selected={selected}
        count={count}
        aria-expanded={open}
        aria-controls={open ? flyoutId : undefined}
        // A click with no pointer behind it (`detail` 0) is Enter or Space, which should land in the flyout.
        onClick={(event: MouseEvent) => openFlyout(event.detail === 0)}
        onMouseEnter={() => openFlyout(false)}
        onMouseLeave={closeSoon}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') openFlyout(true)
          if (event.key === 'Escape') close('keyboard')
        }}
      />
      <NavFlyout
        id={flyoutId}
        title={label}
        anchorEl={trigger}
        open={open}
        autoFocus={openedByKeyboard}
        onClose={close}
        onMouseEnter={() => openFlyout(false)}
        onMouseLeave={closeSoon}
      >
        {children}
      </NavFlyout>
    </li>
  )
}
